<template>
  <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="380" :max-width="420" :nudge-bottom="18" offset-y transition="slide-y-transition">
    <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
      <v-tooltip bottom color="secondary" transition="scale-transition" open-delay="500" :disabled="menu">
        <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <v-btn v-bind="{ ...menuAttrs, ...tooltipAttrs }" v-on="{ ...tooltipOn, ...menuOn }" class="mx-1" height="36" outlined :ripple="false" x-small>
            <v-badge :content="unread" :value="unread" color="red"><v-icon class="text--secondary" size="18">mdi-bell-ring</v-icon></v-badge>
          </v-btn>
        </template>
        <span class="white--text caption">Notificaciones</span>
      </v-tooltip>
    </template>
    <v-responsive class="overflow-y-auto" :height="notifications.length ? '600' : '400'">
      <v-row class="mx-0" align="center" no-gutters>
        <v-col>
          <v-subheader class="pl-5 subtitle-1 text--secondary font-weight-medium">Notificaciones</v-subheader>
        </v-col>
        <v-col class="text-right pr-3" cols="auto" v-if="unread">
          <v-btn class="caption" @click="markAllRead" text color="primary" :ripple="false" :loading="markingAll">Marcar todas como leídas</v-btn>
        </v-col>
      </v-row>

      <v-divider class="ma-0 grey lighten-3" />

      <v-skeleton-loader v-if="loading && !notifications.length" transition="fade-transition" type="list-item-avatar-three-line@3" />

      <template v-else-if="notifications.length">
        <v-row class="mx-0" v-for="notification in notifications" :key="notification.id" align="center" no-gutters>
          <v-col class="pa-0">
            <v-hover v-slot:default="{ hover }" open-delay="200">
              <v-row :class="[hover ? 'grey lighten-4 pt-2' : 'pt-2', notification.url ? 'cursor-pointer' : '']" align="start" justify="center" @click="open(notification)">
                <v-col cols="10" class="py-1 px-0">
                  <v-row align="center" justify="center" no-gutters>
                    <v-col cols="11">
                      <span class="subtitle-2 secondary--text font-weight-medium">{{notification.title}}</span>
                      <span class="d-block body-2">{{notification.message}}</span>
                    </v-col>
                    <v-col cols="1" class="mx-0 text-right">
                      <v-icon v-if="notification.status === 'unread'" color="primary" x-small>mdi-circle</v-icon>
                    </v-col>
                  </v-row>
                  <v-row class="mt-n2" align="center" justify="center" no-gutters>
                    <v-col cols="7" class="ml-0 py-2">
                      <v-chip :color="severityColor(notification.severity)" x-small text-color="white">{{modelLabel(notification.model)}}</v-chip>
                      <v-chip class="ml-1" v-if="notification.event_count > 1" color="grey lighten-1" x-small text-color="white">{{notification.event_count}}</v-chip>
                      <span :class="`${ notification.status === 'unread' ? 'primary--text' : 'secondary--text' } caption font-weight-medium pl-3`">{{notification.created | dateConditional}}</span>
                    </v-col>
                    <v-col cols="5" class="py-0 text-right">
                      <template v-if="hover">
                        <v-btn class="caption" v-if="notification.status === 'unread'" @click.stop="setStatus(notification, 'read')" :ripple="false" text><v-icon class="mr-1" color="primary" small>mdi-check</v-icon>Marcar como leido</v-btn>
                        <v-btn class="caption" v-else @click.stop="setStatus(notification, 'unread')" :ripple="false" text>Marcar como no leido</v-btn>
                      </template>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-hover>

            <v-divider class="mx-0 grey lighten-3" />

          </v-col>
        </v-row>
        <v-col class="py-1 text-center" v-if="hasMore">
          <v-row align="center" justify="center">
            <v-btn @click="seeMore" text color="primary" :ripple="false" :loading="loading">Ver más</v-btn>
          </v-row>
        </v-col>
      </template>
      <v-row v-else align="center" justify="center" style="min-height: 300px">
        <v-col class="mx-5 text-center">
          <v-icon class="mb-5" color="secondary" size="40">mdi-bell-off</v-icon>
          <span class="d-block body-1 text--secondary font-weight-medium">Aún no tienes notificaciones</span>
        </v-col>
      </v-row>
    </v-responsive>
  </v-menu>
</template>
<script>
import { mapState } from 'vuex'
import GenericViewMixin from '@/mixins/GenericViewMixin'
import { notificationModelLabel, notificationSeverityColor } from '@/collections/notifications'

const PAGE_SIZE = 20

/**
 * Notification bell.
 *
 * Deliberately does NOT use ListViewMixin: that mixin reads its page from
 * `$route.query.page`, refetches on every route change and can call
 * `$router.replace`. Sound for a list view that owns the route; wrong for a
 * component mounted in the app bar on every screen, where it would paginate
 * against an unrelated page number and fight the router. Pagination is local.
 */
export default {
  mixins: [
    GenericViewMixin
  ],
  data: () => ({
    menu: false,
    loading: false,
    markingAll: false,
    page: 1,
    count: 0,
    channel: null
  }),
  computed: {
    ...mapState({
      notifications: state => state.accounts.notificationsList,
      unread: state => state.accounts.notificationsUnread,
      memberID: state => state.auth.account.me
    }),
    hasMore () {
      return this.notifications.length < this.count
    }
  },
  created () {
    this.getList()
    this.getUnreadCount()
  },
  mounted () {
    this.subscribe()
  },
  beforeDestroy () {
    this.unsubscribe()
  },
  watch: {
    /**
     * Switching workspace changes the member, and the Pusher channel IS the
     * member id. Without re-subscribing the bell would keep listening on the
     * previous workspace's channel and show its notifications.
     */
    memberID () {
      this.unsubscribe()
      this.subscribe()
      this.page = 1
      this.getList()
      this.getUnreadCount()
    }
  },
  methods: {
    modelLabel: notificationModelLabel,
    severityColor: notificationSeverityColor,
    subscribe () {
      if (!this.memberID || !this.$pusher) {
        return
      }
      this.channel = this.$pusher.subscribe(this.memberID)
      this.channel.bind('new_notification', (data) => {
        /**
         * Prepend the payload instead of refetching: a mass billing run can
         * emit many events, and a refetch per event would hammer the API. The
         * payload carries everything the row renders.
         */
        this.$store.commit('accounts/PREPEND_NOTIFICATION', data)
        this.$store.commit('accounts/ADJUST_NOTIFICATIONS_UNREAD', 1)
      })
    },
    unsubscribe () {
      if (this.channel && this.$pusher) {
        this.$pusher.unsubscribe(this.channel.name)
      }
      this.channel = null
    },
    getList (page = 1) {
      this.loading = true
      return this.$store.dispatch('accounts/LIST', {
        resource: 'notifications',
        query: { page: page, page_size: PAGE_SIZE },
        loader: false
      })
      .then((response) => {
        this.count = response.data.count
        this.page = page
      })
      .finally(() => {
        this.loading = false
      })
    },
    getUnreadCount () {
      return this.$store.dispatch('accounts/NOTIFICATIONS_UNREAD_COUNT')
    },
    seeMore () {
      const next = this.page + 1
      this.loading = true
      this.$store.dispatch('accounts/LIST', {
        resource: 'notifications',
        query: { page: next, page_size: PAGE_SIZE },
        setState: false,
        loader: false
      })
      .then((response) => {
        // Appended, not prepended: these are OLDER notifications from the next
        // page and belong at the bottom of the list.
        this.$store.commit('accounts/APPEND_NOTIFICATIONS', response.data.results)
        this.count = response.data.count
        this.page = next
      })
      .finally(() => {
        this.loading = false
      })
    },
    setStatus (notification, status) {
      if (notification.status === status) {
        return
      }
      this.$store.commit('accounts/ADJUST_NOTIFICATIONS_UNREAD', status === 'read' ? -1 : 1)

      return this.$store.dispatch('accounts/UPDATE', {
        resource: 'notifications',
        payload: { status: status },
        id: notification.id
      })
      .catch(() => {
        // Roll the optimistic counter back and resync with the server.
        this.$store.commit('accounts/ADJUST_NOTIFICATIONS_UNREAD', status === 'read' ? 1 : -1)
        this.getUnreadCount()
      })
    },
    markAllRead () {
      this.markingAll = true
      this.$store.dispatch('accounts/NOTIFICATIONS_MARK_ALL_READ')
      .then(() => {
        this.getList(1)
      })
      .catch((error) => {
        this.$dialog.message.warning(this.parseErrors(error.response.data))
      })
      .finally(() => {
        this.markingAll = false
      })
    },
    open (notification) {
      if (notification.status === 'unread') {
        this.setStatus(notification, 'read')
      }
      if (!notification.url) {
        return
      }
      this.menu = false
      if (this.$route.fullPath !== notification.url) {
        this.$router.push(notification.url)
      }
    }
  }
}
</script>
<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
